import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight } from 'lucide-react';
import { useTranslation } from '../../../../lib/i18n';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { queryKeys } from '../../../../lib/query-client';
import { connectorDescriptionKey, fetchConnectors } from '../../../../lib/connectors';
import { connectorsPath } from '../../../../lib/connectors-route';
import {
  CONNECT_POLL_INTERVAL_MS,
  connectTapStorageKey,
  deriveConnectorCardState,
  isToolkitConnected,
  shouldPollWhileWaiting,
  type ConnectorCardData,
} from '../../../../lib/connector-card';
import { shouldArmConnectHandoff } from '../../../../lib/connect-handoff';
import { ConnectorLogo } from '../../connectors/ConnectorLogo';
import { Spinner } from '../../../Spinner';
import { Button, Card, TextButton, Typography } from '../../ui';
import { CHAT_HOME_BI } from '../message-utils';
import styles from './ConnectorConnectCard.module.css';

// The in-chat connector connect card — rendered under an assistant reply carrying
// `metadata.connectorCard` (a turn whose ConnectApp tool minted a Composio OAuth link).
// The reply text deliberately carries no URL, so this card is the only way to connect.
// It is LIVE: state derives from the shared connectors status data + the local tap
// marker + the link's age (see lib/connector-card.ts — connected > expired > waiting >
// offered). All pure logic lives in that dependency-free module.

// Lucide's `check`, scaled into the 24px disc — same resting badge as MilestoneCard.
const CHECK_PATH = 'M6.67 12 L10 15.33 L17.33 8';

function readTapMarker(messageId: string | undefined): number | null {
  if (!messageId || typeof window === 'undefined') return null;
  try {
    const value = window.localStorage.getItem(connectTapStorageKey(messageId));
    if (!value) return null;
    const at = Date.parse(value);
    // A marker we can't parse still means "this browser tapped Connect" — it just can't
    // scope the poll window, so it doesn't poll (focus refetch still covers it).
    return Number.isFinite(at) ? at : 0;
  } catch {
    return null;
  }
}

export function ConnectorConnectCard({
  card,
  messageId,
  onRequestNewLink,
  onConnected,
}: {
  card: ConnectorCardData;
  /** The row's persisted id (finalId when streamed) — scopes the tap marker to this message. */
  messageId?: string;
  /** Expired state's "Get a new link": send the localized ask as a normal user turn. Absent (read-only surface) => line only. */
  onRequestNewLink?: (text: string) => void;
  /** Fired the moment this card observes the connect — the transcript shows a thinking line while the genome wakes the loop. */
  onConnected?: () => void;
}) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const [tappedAt, setTappedAt] = useState<number | null>(() => readTapMarker(messageId));
  const tapped = tappedAt !== null;

  // Whether this card is still trying to observe a connect (drives the focus refetch and
  // the 5s poll). Derived WITHOUT the status data — the data-aware check runs inside
  // `refetchInterval`, which sees the freshest read and stops the moment it lands.
  const awaitingConnect = deriveConnectorCardState({ connected: false, tapped, mintedAt: card.mintedAt, now: Date.now() }) === 'waiting';

  const { data } = useQuery({
    queryKey: queryKeys.connectors,
    queryFn: fetchConnectors,
    refetchOnWindowFocus: awaitingConnect,
    refetchInterval: (latest) => {
      if (!awaitingConnect || tappedAt === null) return false;
      if (latest && isToolkitConnected(latest.connectors, card.toolkit)) return false;
      return shouldPollWhileWaiting(tappedAt, Date.now()) ? CONNECT_POLL_INTERVAL_MS : false;
    },
  });

  const connected = isToolkitConnected(data?.connectors, card.toolkit);
  const state = deriveConnectorCardState({ connected, tapped, mintedAt: card.mintedAt, now: Date.now() });

  // A transition arms the handoff, and so does a FIRST read that is already connected when this browser
  // tapped Connect recently — that is the reload-after-authorizing case, where the ref has been reset and
  // there is no transition left to see. A thread reopened long after the connect still spins nothing.
  const wasConnectedRef = useRef<boolean | undefined>(undefined);
  useEffect(() => {
    if (shouldArmConnectHandoff(wasConnectedRef.current, connected, tappedAt)) onConnected?.();
    wasConnectedRef.current = connected;
  }, [connected, onConnected, tappedAt]);

  const onConnect = () => {
    logPageClickBi({ ...CHAT_HOME_BI, button_name: 'connector-card-connect' });
    window.open(card.connectUrl, '_blank', 'noopener');
    const now = Date.now();
    if (messageId) {
      try {
        window.localStorage.setItem(connectTapStorageKey(messageId), new Date(now).toISOString());
      } catch {
        // storage unavailable — the in-memory tap still enters waiting for this session
      }
    }
    setTappedAt(now);
  };

  const onGetNewLink = () => {
    logPageClickBi({ ...CHAT_HOME_BI, button_name: 'connector-card-new-link' });
    onRequestNewLink?.(t('chat.connectorCard.renewMessage', { name: card.name }));
  };

  const onBrowseAll = () => {
    logPageClickBi({ ...CHAT_HOME_BI, button_name: 'connector-card-browse-all' });
  };

  if (state === 'connected') {
    return (
      <Card className={`${styles.card} ${styles.connected}`}>
        <div className={styles.row}>
          <svg className={styles.badge} width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden focusable="false">
            <circle className={styles.disc} cx="12" cy="12" r="12" />
            <path className={styles.check} d={CHECK_PATH} />
          </svg>
          <div className={styles.text}>
            <Typography variant="h4" component="div" color="strong">
              {t('chat.connectorCard.connectedTitle', { name: card.name })}
            </Typography>
            <Typography variant="p3" color="strong">
              {t('chat.connectorCard.connectedBody')}
            </Typography>
          </div>
        </div>
      </Card>
    );
  }

  const descriptionKey = connectorDescriptionKey({ toolkit: card.toolkit, connected: false });
  const localizedDescription = descriptionKey ? t(descriptionKey) : null;
  const supportingLine =
    state === 'waiting'
      ? t('chat.connectorCard.waiting')
      : state === 'expired'
        ? t('chat.connectorCard.expired')
        : localizedDescription && localizedDescription !== descriptionKey
          ? localizedDescription
          : t('chat.connectorCard.genericDescription');

  return (
    <Card className={styles.card}>
      <div className={styles.row}>
        {/* Native's `connectorBadgePlate` — fixed white in BOTH themes (iOS ConnectorConnectCard
            `logoTile`, Android `ConnectorConnectCard`), NOT the connectors screen's page-level
            `--connector-plate`. A chat card is a light object in the conversation either way. */}
        <ConnectorLogo
          connector={{ toolkit: card.toolkit, logo: card.logoUrl, connected: false }}
          name={card.name}
          size={40}
          tileBackground="var(--ds-white)"
        />
        <div className={styles.text}>
          <Typography variant="h4" component="div" color="strong">
            {card.name}
          </Typography>
          <Typography variant="p3" color="secondary">
            {supportingLine}
          </Typography>
        </div>
        {state === 'waiting' ? (
          <span className={styles.spinnerSlot} role="status" aria-label={t('common.connecting')}>
            <Spinner size={18} />
          </span>
        ) : state === 'expired' ? (
          onRequestNewLink && <Button onClick={onGetNewLink}>{t('chat.connectorCard.getNewLink')}</Button>
        ) : (
          <Button onClick={onConnect}>{t('common.connect')}</Button>
        )}
      </div>
      <TextButton
        size="small"
        href={connectorsPath({ tab: 'tools' })}
        onClick={onBrowseAll}
        endIcon={<ChevronRight className="dir-flip" size={16} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />}
      >
        {t('chat.connectorCard.browseAll')}
      </TextButton>
    </Card>
  );
}
